import React, { useMemo, useState, useEffect, useRef, createContext, useContext } from 'react';
import { Card, Button } from '@/components/ui';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { ToggleSection } from '@/components/form/ToggleSection';
import { cn } from '@/lib/utils';
import { AnimatePresence } from 'framer-motion';
import { type RateMultiplier, type ShortTurnConfig as ShortTurnConfigType, type Role } from '@/types';
import type { ExtendedTier } from '@/types/ExtendedTier';
import { generateMarkdownVariables } from './utils/markdownVariables';
import { getRatedRoles, getSampleRole } from '@/utils/rateCalculations';
import { ShortTurnHeader } from './components/ShortTurnHeader';
import { CustomizedFieldIndicator } from '../../form/CustomizedFieldIndicator';
import { MinimumRestPeriodField } from './components/MinimumRestPeriodField';
import { SurchargeMethodSelector } from './components/SurchargeMethodSelector';
import { SurchargePeriodField } from './components/SurchargePeriodField';
import { FixedMultiplierField } from './components/FixedMultiplierField';
import { ShortTurnOffsetAmountControl } from './components/ShortTurnOffsetAmountControl';
import { FixedDollarField } from './components/FixedDollarField';
import { HelpInfoCallout } from './components/HelpInfoCallout';
import { HourlyPremiumSection } from '../shared/HourlyPremiumSection';
import { ExtendedOvertimeSection } from '../shared/ExtendedOvertimeSection';
import { OvertimeLadderVisual } from '../shared/help-graphics/OvertimeLadderVisual';
import { OvertimeLadderComparison } from '../shared/help-graphics/OvertimeLadderComparison';
import { ApplicableRolesRow } from '../shared/AdvancedSettingsSection';
import { getRoleId } from '../shared/utils';
import { Settings, DollarSign } from 'lucide-react';
import { ConfigSection } from '../shared/ConfigSection';
import { hasAnyHalfDayRoles } from '../shared/utils';
import { SurchargeMethodPanel } from '../shared/surcharge/SurchargeMethodPanel';
import { useMethodSwapAnimation } from '../shared/surcharge/useMethodSwapAnimation';
import { useOvertimeLadderInfo } from '@/hooks/useOvertimeLadderInfo';
import { useOverflowAnchorLock } from '@/hooks/useOverflowAnchorLock';
import { fieldDescriptions } from './utils/loadDescriptions';
import { shortTurnModes } from './utils/surchargeMethods';

export interface ShortTurnConfigFormProps {
  usesShortTurn: boolean;
  onUsesShortTurnChange: (checked: boolean) => void;
  roles: Role[];
  shortTurnConfig: ShortTurnConfigType;
  onShortTurnConfigChange: (updates: Partial<ShortTurnConfigType>) => void;
  rateMultipliers: RateMultiplier[];
  hasAdditionalMultipliers: boolean;
  minimumBillableHours?: number | null;
  
  // Validated input props
  defaultBreakInputProps: Record<string, unknown>;
  defaultBreakError?: React.ReactNode;
  shortTurnFixedSurchargeAmountProps: Record<string, unknown>;
  shortTurnFixedSurchargeAmountError?: React.ReactNode;
  shortTurnPremiumValueProps?: Record<string, unknown>;
  shortTurnPremiumValueError?: React.ReactNode;
  
  // Errors
  surchargeTypeError?: boolean;
  noMultiplierSelectedError?: boolean;

  // Customization props
  isCustomized?: boolean;
  onRevert?: () => void;
  customizedFields?: Record<string, boolean>;
  title?: string;
  description?: string;
  noCard?: boolean;
  
  // Context-specific labels
  context?: 'user' | 'client';
  hideToggle?: boolean;
  
  // Role-specific surcharges
  shortTurnApplicableRoleIds?: string[] | null;
  onShortTurnApplicableRoleIdsChange?: (roleIds: string[] | null) => void;

  // Extended overtime (per-surcharge)
  highestOvertimeRate?: number;
  defaultExtendedTiers?: ExtendedTier[];
}

// Context for ShortTurnConfigForm
const ShortTurnConfigContext = createContext<Omit<ShortTurnConfigFormProps, 'usesShortTurn' | 'onUsesShortTurnChange'> & { markdownVariables: Record<string, unknown>; hasMultipliersWithThresholds: boolean; ratedRoleNames: string[]; activeRoleIndex: number; setExampleRoleIndex: React.Dispatch<React.SetStateAction<number | undefined>>; resolvedRoleIndex: number } | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useShortTurnConfig = () => {
  const context = useContext(ShortTurnConfigContext);
  if (!context) {
    throw new Error('useShortTurnConfig must be used within a ShortTurnConfigProvider');
  }
  return context;
};

const ShortTurnConfigFormComponent: React.FC<ShortTurnConfigFormProps> = ({
  usesShortTurn,
  onUsesShortTurnChange,
  roles,
  shortTurnConfig,
  onShortTurnConfigChange,
  rateMultipliers,
  hasAdditionalMultipliers,
  defaultBreakInputProps,
  defaultBreakError,
  shortTurnFixedSurchargeAmountProps,
  shortTurnFixedSurchargeAmountError,
  shortTurnPremiumValueProps,
  shortTurnPremiumValueError,
  surchargeTypeError,
  noMultiplierSelectedError,
  isCustomized,
  onRevert,
  customizedFields = {},
  title = "Short Turn Surcharge",
  description = "Configure surcharges when rest between consecutive work days falls below your minimum.",
  noCard,
  context = 'user',
  minimumBillableHours,
  hideToggle,
  shortTurnApplicableRoleIds,
  onShortTurnApplicableRoleIdsChange,
  highestOvertimeRate: propHighestOvertimeRate,
  defaultExtendedTiers,
}) => {

  const { thresholdMultipliers, highestOvertimeRate, highestOvertimeTierName, maxExtendedTiers } = useOvertimeLadderInfo(rateMultipliers, propHighestOvertimeRate);
  const hasMultipliersWithThresholds = thresholdMultipliers.length > 0;

  const anyHalfDayRoles = useMemo(() => hasAnyHalfDayRoles(roles, shortTurnApplicableRoleIds), [roles, shortTurnApplicableRoleIds]);

  const configureExtendedRef = useRef<(() => void) | null>(null);

  // Compute dynamic variables for the Extended Overtime help sheet
  const extendedOtHelpVars = useMemo(() => {
    const straightTimeMultiplier = rateMultipliers.find(m => m.code === 'STRAIGHT_TIME');
    const sortedLadder = [straightTimeMultiplier, ...thresholdMultipliers]
      .filter(Boolean)
      .sort((a, b) => (a!.defaultMultiplier ?? 0) - (b!.defaultMultiplier ?? 0));

    const ladderTop = sortedLadder[sortedLadder.length - 1];
    const ladderTopName = ladderTop?.name ?? 'Straight Time';
    const ladderTopRate = ladderTop?.defaultMultiplier ?? 1;

    const sortedExtTiers = [...(shortTurnConfig.extendedOvertimeTiers ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    const ladderStr = sortedLadder
      .map(m => `${m!.name} (${m!.defaultMultiplier}x)`)
      .join(' → ');

    const methodName = shortTurnModes.find(m => m.value === shortTurnConfig.surchargeType)?.label ?? 'Carry Over';

    // For Fixed Start, use the configured multiplier if available
    const fixedStartMultiplier = shortTurnConfig.floorTierCode
      ? rateMultipliers.find(m => m.code === shortTurnConfig.floorTierCode)
      : null;

    // Build bracket labels from actual thresholds (e.g. "0–10 hrs", "10+ hrs")
    // Mirror backend logic: if ANY multiplier has a per-shift threshold, use those; otherwise per-week
    const hasPerShiftThresholds = thresholdMultipliers.some(m => m.defaultPerShiftThresholdHours != null);
    const thresholds = thresholdMultipliers
      .map(m => hasPerShiftThresholds ? m.defaultPerShiftThresholdHours : m.defaultPerWeekThresholdHours)
      .filter((t): t is number => t != null)
      .sort((a, b) => a - b);

    const bracketLabels: string[] = [];
    if (thresholds.length > 0) {
      bracketLabels.push(`0–${thresholds[0]} hrs`);
      for (let i = 0; i < thresholds.length - 1; i++) {
        bracketLabels.push(`${thresholds[i]}–${thresholds[i + 1]} hrs`);
      }
      bracketLabels.push(`${thresholds[thresholds.length - 1]}+ hrs`);
    } else {
      bracketLabels.push('All hours');
    }

    // Compute offset tier index based on surcharge method
    let offsetTierIndex: number;
    if (shortTurnConfig.surchargeType === 'offset_overtime' && fixedStartMultiplier) {
      offsetTierIndex = sortedLadder.findIndex(m => m!.code === shortTurnConfig.floorTierCode);
      if (offsetTierIndex < 0) offsetTierIndex = sortedLadder.length - 1;
    } else {
      // Carry Over & Bump Up: offset = top of ladder
      offsetTierIndex = sortedLadder.length - 1;
    }

    const ladderSteps = sortedLadder.map(m => ({ name: m!.name, rate: m!.defaultMultiplier ?? 1 }));
    const extTierSteps = sortedExtTiers.map(t => ({ name: t.name, rate: t.rate }));
    const helpSheetMaxExtendedTiers = shortTurnConfig.surchargeType === 'bump_up'
      ? maxExtendedTiers + 1
      : maxExtendedTiers;

    return {
      surcharge_method_name: methodName,
      ladder_str: ladderStr,
      ladder_top_name: ladderTopName,
      ladder_top_rate: ladderTopRate,
      injectables: {
        ladder_visual: (
          <OvertimeLadderVisual
            ladder={ladderSteps}
            extendedTiers={extTierSteps.length > 0 ? extTierSteps : undefined}
          />
        ),
        ladder_comparison: (
          <OvertimeLadderComparison
            ladder={ladderSteps}
            extendedTiers={extTierSteps.length > 0 ? extTierSteps : undefined}
            offsetTierIndex={offsetTierIndex}
            bracketLabels={bracketLabels}
            methodName={methodName}
            onConfigureExtended={() => configureExtendedRef.current?.()}
            editableExtendedTiers={shortTurnConfig.extendedOvertimeTiers ?? []}
            onEditableExtendedTiersChange={(tiers) => onShortTurnConfigChange({ extendedOvertimeTiers: tiers })}
            editableExtendedTiersContext={context}
            editableExtendedTiersMax={helpSheetMaxExtendedTiers}
            extendedOvertimeEnabled={shortTurnConfig.extendedOvertimeEnabled ?? false}
            onExtendedOvertimeToggle={(v) => onShortTurnConfigChange({ extendedOvertimeEnabled: v })}
            belowGraphContent={(
              <>
                <HelpInfoCallout title="Cap">
                  When a short-turn surcharge climbs past the top of your overtime ladder, it caps there by default. Extended tiers allow it to continue climbing.
                </HelpInfoCallout>
                <HelpInfoCallout title="Tier Limit">
                  The number of reachable tiers depends on how deep your overtime ladder is.
                </HelpInfoCallout>
              </>
            )}
          />
        ),
      },
    };
  }, [rateMultipliers, thresholdMultipliers, maxExtendedTiers, context, shortTurnConfig.extendedOvertimeTiers, shortTurnConfig.extendedOvertimeEnabled, shortTurnConfig.surchargeType, shortTurnConfig.floorTierCode, onShortTurnConfigChange]);

  const [triggerConditionsOpen, setTriggerConditionsOpen] = useState(true);
  const [surchargeConfigOpen, setSurchargeConfigOpen] = useState(true);

  // Dynamic labels based on context
  const labels = {
    user: {
      enableLabel: "Short Turn Surcharge",
      enableDescription: "Charge extra when rest between consecutive work days is below your minimum",
      defaultBreakLabel: "Minimum Rest Period",
      defaultBreakDescription: "The minimum rest you require between the end of one work day and the start of the next for a single client.",
      modeLabel: "Short-Turn Surcharge Method",
      fixedSurchargeLabel: "Fixed Surcharge Amount",
      fixedSurchargeDescription: "Set a fixed dollar amount to add for short turn shifts",
      multiplierLabel: "Starting Rate Multiplier",
      revertText: "Revert to Profile Defaults"
    },
    client: {
      enableLabel: "Short Turn Surcharge",
      enableDescription: "Charge extra when rest between consecutive work days is below the minimum",
      defaultBreakLabel: "Minimum Rest Period",
      defaultBreakDescription: "Shifts on consecutive work days with less than this gap will trigger a short turn surcharge for this client.",
      modeLabel: "Short-Turn Surcharge Method",
      fixedSurchargeLabel: "Fixed Surcharge Amount",
      fixedSurchargeDescription: "Set a fixed dollar amount to add for short turn shifts for this client",
      multiplierLabel: "Starting Rate Multiplier",
      revertText: "Revert to Profile Defaults"
    }
  };
  
  const currentLabels = labels[context];

  const {
    containerStyle: methodContainerStyle,
    containerRef: methodContentContainerRef,
    activePanelRef: activeMethodPanelRef,
    fadeDuration: methodFadeDuration,
  } = useMethodSwapAnimation(shortTurnConfig.surchargeType);

  const [exampleRoleIndex, setExampleRoleIndex] = useState<number | undefined>(undefined);
  const anchorLockRef = useRef<HTMLDivElement>(null);
  useOverflowAnchorLock(anchorLockRef);

  // All rated roles (for markdown variable generation — indexes must match getRatedRoles)
  const allRatedRoleNames = useMemo(() => getRatedRoles(roles).map(r => r.name), [roles]);

  // Indices of rated roles that are active in the ApplicableRolesField.
  // Reset exampleRoleIndex when applicable roles change so it doesn't go stale.
  const applicableRatedIndices = useMemo(() => {
    if (!shortTurnApplicableRoleIds) return allRatedRoleNames.map((_, i) => i);
    const rated = getRatedRoles(roles);
    return rated
      .map((r, i) => ({ role: r, ratedIndex: i, origIndex: roles.indexOf(r) }))
      .filter(({ role, origIndex, ratedIndex }) =>
        shortTurnApplicableRoleIds.includes(getRoleId(role, origIndex >= 0 ? origIndex : ratedIndex))
      )
      .map(({ ratedIndex }) => ratedIndex);
  }, [roles, shortTurnApplicableRoleIds, allRatedRoleNames]);

  // Role names shown in the cycler (only applicable roles)
  const ratedRoleNames = useMemo(
    () => applicableRatedIndices.map(i => allRatedRoleNames[i]),
    [applicableRatedIndices, allRatedRoleNames],
  );

  // Reset cycled role when the applicable set changes
  useEffect(() => { setExampleRoleIndex(undefined); }, [shortTurnApplicableRoleIds]);

  const activeRoleIndex = useMemo(() => {
    if (exampleRoleIndex != null && exampleRoleIndex < ratedRoleNames.length) return exampleRoleIndex;
    const rated = getRatedRoles(roles);
    const defaultRole = getSampleRole(roles);
    const defaultRatedIdx = rated.indexOf(defaultRole);
    // Map the default role's rated-index to its position in the applicable list
    const posInApplicable = applicableRatedIndices.indexOf(defaultRatedIdx);
    return Math.max(0, posInApplicable);
  }, [roles, exampleRoleIndex, ratedRoleNames.length, applicableRatedIndices]);

  // Translate filtered-list index → full getRatedRoles() index for markdown generation.
  // Always resolve (even when exampleRoleIndex is undefined) so the default sample role
  // respects the applicable-roles filter instead of falling back to getSampleRole().
  const resolvedRoleIndex = useMemo(
    () => applicableRatedIndices[activeRoleIndex] ?? applicableRatedIndices[0],
    [activeRoleIndex, applicableRatedIndices],
  );

  // Calculate dynamic variables for markdown content
  const markdownVariables = useMemo(() => {
    return generateMarkdownVariables(rateMultipliers, shortTurnConfig, roles, minimumBillableHours, resolvedRoleIndex);
  }, [rateMultipliers, shortTurnConfig, roles, minimumBillableHours, resolvedRoleIndex]);

  // Create context value with all the props and computed values
  const contextValue = useMemo(() => ({
    roles,
    shortTurnConfig,
    onShortTurnConfigChange,
    rateMultipliers,
    hasAdditionalMultipliers,
    minimumBillableHours,
    defaultBreakInputProps,
    defaultBreakError,
    shortTurnFixedSurchargeAmountProps,
    shortTurnFixedSurchargeAmountError,
    shortTurnPremiumValueProps,
    shortTurnPremiumValueError,
    surchargeTypeError,
    noMultiplierSelectedError,
    isCustomized,
    onRevert,
    customizedFields,
    title,
    description,
    noCard,
    context,
    hideToggle,
    shortTurnApplicableRoleIds,
    onShortTurnApplicableRoleIdsChange,
    markdownVariables,
    hasMultipliersWithThresholds,
    ratedRoleNames,
    activeRoleIndex,
    setExampleRoleIndex,
    resolvedRoleIndex,
  }), [
    roles, shortTurnConfig, onShortTurnConfigChange, rateMultipliers, hasAdditionalMultipliers,
    minimumBillableHours, defaultBreakInputProps, defaultBreakError, shortTurnFixedSurchargeAmountProps,
    shortTurnFixedSurchargeAmountError, shortTurnPremiumValueProps, shortTurnPremiumValueError,
    surchargeTypeError, noMultiplierSelectedError,
    isCustomized, onRevert, customizedFields, title, description, noCard, context, hideToggle,
    shortTurnApplicableRoleIds, onShortTurnApplicableRoleIdsChange, markdownVariables, hasMultipliersWithThresholds,
    ratedRoleNames, activeRoleIndex, setExampleRoleIndex, resolvedRoleIndex,
  ]);

  const extendedOTBaseProps = {
    enabled: shortTurnConfig.extendedOvertimeEnabled ?? false,
    onEnabledChange: (v: boolean) => onShortTurnConfigChange({ extendedOvertimeEnabled: v }),
    tiers: shortTurnConfig.extendedOvertimeTiers ?? [],
    onTiersChange: (tiers: typeof shortTurnConfig.extendedOvertimeTiers) => onShortTurnConfigChange({ extendedOvertimeTiers: tiers }),
    highestOvertimeRate,
    highestOvertimeTierName,
    context,
    defaultTiers: defaultExtendedTiers,
    helpContent: fieldDescriptions.extended_overtime,
    helpFieldNamePrefix: "Short Turn" as const,
    helpVariables: extendedOtHelpVars,
    helpInjectableComponents: extendedOtHelpVars.injectables,
    configureRef: configureExtendedRef,
  };

  const formFields = (
    <ShortTurnConfigContext.Provider value={contextValue}>
      <div className="space-y-3">
      <ConfigSection
        title="Trigger Conditions"
        description="Configure when short turn surcharge applies."
        icon={Settings}

        isOpen={triggerConditionsOpen}
        onOpenChange={setTriggerConditionsOpen}
      >
        <div className="space-y-4">
          <MinimumRestPeriodField />
          <SurchargePeriodField disabled={shortTurnConfig.surchargeType === 'fixed_dollar'} />
          <ApplicableRolesRow
            roles={roles || []}
            applicableRoleIds={shortTurnApplicableRoleIds ?? null}
            onApplicableRoleIdsChange={onShortTurnApplicableRoleIdsChange || (() => {})}
            isCustomized={customizedFields.applicable_role_ids}
            context={context}
            featureName="short-turn surcharge"
            showHalfDayOption={anyHalfDayRoles}
            applyOnHalfDays={shortTurnConfig.applyOnHalfDays}
            onApplyOnHalfDaysChange={(checked) => onShortTurnConfigChange({ applyOnHalfDays: checked })}
            className="mt-0"
          />
        </div>
      </ConfigSection>

      <ConfigSection
        title="Surcharge Configuration"
        description="Configure how the short turn surcharge is calculated."
        icon={DollarSign}

        isOpen={surchargeConfigOpen}
        onOpenChange={setSurchargeConfigOpen}
      >
        <div>
          <SurchargeMethodSelector />

          <div
            ref={methodContentContainerRef}
            className="overflow-hidden"
            style={methodContainerStyle}
            data-anim-audit="short-turn-method-panel-container"
          >
            <div className="relative grid">
              <AnimatePresence mode="sync" initial={false}>
                {shortTurnConfig.surchargeType === 'hourly_premium' && (
                  <SurchargeMethodPanel
                    key="hourly_premium"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <div className="space-y-4">
                      <HourlyPremiumSection
                        config={shortTurnConfig}
                        onConfigChange={(updates) => onShortTurnConfigChange(updates as Partial<ShortTurnConfigType>)}
                        premiumValueProps={shortTurnPremiumValueProps || {}}
                        premiumValueError={shortTurnPremiumValueError}
                        customizedFields={customizedFields}
                      />
                    </div>
                  </SurchargeMethodPanel>
                )}

                {shortTurnConfig.surchargeType === 'fixed_dollar' && (
                  <SurchargeMethodPanel
                    key="fixed_dollar"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <div className="space-y-4">
                      <FixedDollarField />
                    </div>
                  </SurchargeMethodPanel>
                )}

                {shortTurnConfig.surchargeType === 'guaranteed_rate' && (
                  <SurchargeMethodPanel
                    key="guaranteed_rate"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <div className="space-y-4">
                      {/* TODO(rate-offset-unification): migrate to OffsetConfigSection — see docs/superpowers/notes/2026-04-13-shared-component-adoption.md */}
                      <FixedMultiplierField isFloor={true} />
                    </div>
                  </SurchargeMethodPanel>
                )}

                {shortTurnConfig.surchargeType === 'carry_over' && (
                  <SurchargeMethodPanel
                    key="carry_over"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <div className="space-y-4">
                      <ExtendedOvertimeSection {...extendedOTBaseProps} maxTiers={maxExtendedTiers} />
                    </div>
                  </SurchargeMethodPanel>
                )}

                {shortTurnConfig.surchargeType === 'bump_up' && (
                  <SurchargeMethodPanel
                    key="bump_up"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <div className="space-y-4">
                      <ExtendedOvertimeSection {...extendedOTBaseProps} maxTiers={maxExtendedTiers + 1} />
                    </div>
                  </SurchargeMethodPanel>
                )}

                {shortTurnConfig.surchargeType === 'offset_overtime' && (
                  <SurchargeMethodPanel
                    key="offset_overtime"
                    activePanelRef={activeMethodPanelRef}
                    transitionDuration={methodFadeDuration}
                  >
                    <div className="space-y-4">
                      {/* TODO(rate-offset-unification): migrate to OffsetConfigSection — see docs/superpowers/notes/2026-04-13-shared-component-adoption.md */}
                      <ShortTurnOffsetAmountControl />
                      <ExtendedOvertimeSection {...extendedOTBaseProps} maxTiers={maxExtendedTiers} />
                    </div>
                  </SurchargeMethodPanel>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </ConfigSection>
      </div>
    </ShortTurnConfigContext.Provider>
  );

  const withAnchorLock = (node: React.ReactNode) => (
    <div
      ref={anchorLockRef}
      data-anim-audit="short-turn-config-anchor-lock-root"
      style={{ display: 'contents' }}
    >
      {node}
    </div>
  );

  if (hideToggle) {
    return withAnchorLock(formFields);
  }

  const content = (
    <div className="space-y-4">
      <ShortTurnHeader
        title={title}
        description={description}
        isCustomized={isCustomized}
        context={context}
        noCard={noCard}
      />
      
      <ToggleSection
        id="usesShortTurn"
        label={
          <div className="flex items-center gap-1.5">
            <span>{currentLabels.enableLabel}</span>
            <CustomizedFieldIndicator isCustomized={customizedFields.uses_short_turn} context={context} />
          </div>
        }
        description={currentLabels.enableDescription}
        checked={usesShortTurn}
        onCheckedChange={onUsesShortTurnChange}
      >
        <AnimatedCollapse open={usesShortTurn}>
          {formFields}
        </AnimatedCollapse>
      </ToggleSection>
      
      <AnimatedCollapse open={!!(isCustomized && onRevert)}>
            <div className="pt-4 border-t flex justify-center">
              <Button variant="link" className="p-0 h-auto text-muted-foreground" onClick={onRevert}>
                {currentLabels.revertText}
              </Button>
            </div>
      </AnimatedCollapse>
    </div>
  );
  
  if (noCard) {
    return withAnchorLock(content);
  }

  return withAnchorLock(
    <Card
      className={cn(
        "animate-fade-in rounded equipment-panel p-3 pb-4",
        "md:p-6 md:max-w-3xl md:mx-auto"
      )}
    >
      {content}
    </Card>
  );
};

export const ShortTurnConfigForm = React.memo(ShortTurnConfigFormComponent);
ShortTurnConfigForm.displayName = 'ShortTurnConfigForm';
